import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Form,
  Alert,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { useGetProductsBySubcategoryQuery } from "../slices/productApiSlice";

const PAGE_SIZE = 10;

const ProductListScreen = () => {
  const { subcategory } = useParams();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [allProducts, setAllProducts] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  // Filter states
  const [selectedVendor, setSelectedVendor] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");

  // Reset pagination and filters on subcategory change
  useEffect(() => {
    setPage(1);
    setAllProducts([]);
    setHasMore(true);
    setTotalCount(0);
    setSelectedVendor("");
    setMinPrice("");
    setMaxPrice("");
  }, [subcategory]);

  const { data, isLoading, isFetching, error, refetch } =
    useGetProductsBySubcategoryQuery(
      { subcategory, page, limit: PAGE_SIZE },
      { keepPreviousData: true }
    );

  // Update products when data changes or page changes
  useEffect(() => {
    if (data?.products?.length > 0) {
      setAllProducts((prev) =>
        page === 1 ? data.products : [...prev, ...data.products]
      );
      setTotalCount(data.totalCount);

      const currentLength =
        page === 1 ? data.products.length : allProducts.length + data.products.length;
      setHasMore(currentLength < data.totalCount);
    } else if (page === 1) {
      setAllProducts([]);
      setHasMore(false);
      setTotalCount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, page]);

  // Vendor dropdown options
  const vendorOptions = useMemo(() => {
    const map = new Map();
    allProducts.forEach((p) => {
      if (p.vendorName) {
        const key = p.vendorName.trim().toLowerCase();
        if (!map.has(key)) {
          map.set(key, p.vendorName.trim());
        }
      }
    });
    return Array.from(map.entries()).map(([value, label]) => ({
      value,
      label,
    }));
  }, [allProducts]);

  // Filtered products
  const filteredProducts = useMemo(() => {
    if (!allProducts.length) return [];

    return allProducts.filter((product) => {
      const vendor = product.vendorName?.trim().toLowerCase() || "";
      const matchesVendor = selectedVendor ? vendor === selectedVendor : true;
      const matchesMin = minPrice
        ? product.price >= parseFloat(minPrice)
        : true;
      const matchesMax = maxPrice
        ? product.price <= parseFloat(maxPrice)
        : true;
      return matchesVendor && matchesMin && matchesMax;
    });
  }, [allProducts, selectedVendor, minPrice, maxPrice]);

  // Load more
  const loadMore = () => {
    if (!isFetching && hasMore) {
      setPage((prev) => prev + 1);
    }
  };

  // Navigate to detail
  const goToProductDetail = useCallback(
    (id) => navigate(`/product/${id}`),
    [navigate]
  );

  if ((isLoading && page === 1) || !data) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="text-center mt-5">
        <Alert variant="danger">Failed to load products.</Alert>
        <Button onClick={refetch} variant="link">
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <Container className="my-4">
      <Button
        variant="outline-secondary"
        onClick={() => navigate("/user-categories")}
        className="mb-3"
      >
        ← Back to Category
      </Button>

      <h3 className="mb-4 text-primary">
        Products in “{subcategory}” ({totalCount})
      </h3>

      {/* Filters */}
      <Card className="p-3 mb-4 shadow-sm">
        <h5>🔍 Filter Products</h5>
        <Row className="g-3 mt-2">
          <Col md={4}>
            <Form.Group controlId="vendorSelect">
              <Form.Label>Vendor</Form.Label>
              <Form.Select
                value={selectedVendor}
                onChange={(e) => setSelectedVendor(e.target.value)}
              >
                <option value="">All Vendors</option>
                {vendorOptions.map((v) => (
                  <option key={v.value} value={v.value}>
                    {v.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group controlId="minPrice">
              <Form.Label>Min Price</Form.Label>
              <Form.Control
                type="number"
                placeholder="e.g. 100"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
              />
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group controlId="maxPrice">
              <Form.Label>Max Price</Form.Label>
              <Form.Control
                type="number"
                placeholder="e.g. 1000"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
        <div className="text-center mt-3">
          <Button
            variant="link"
            onClick={() => {
              setSelectedVendor("");
              setMinPrice("");
              setMaxPrice("");
            }}
          >
            Clear Filters
          </Button>
        </div>
      </Card>

      {/* Product List */}
      <Row className="g-4">
        {filteredProducts.length === 0 && !isFetching && (
          <div className="text-center text-muted">
            <p>No products match your filters.</p>
            <Button
              size="sm"
              onClick={() => {
                setSelectedVendor("");
                setMinPrice("");
                setMaxPrice("");
              }}
            >
              Clear Filters
            </Button>
          </div>
        )}

        {filteredProducts.map((item, index) => {
          const cardColors = [
            "#dbeafe",
            "#ede9fe",
            "#e0f7fa",
            "#f3e8ff",
            "#e0ecf8",
            "#f0f4ff",
          ];
          const bg = cardColors[index % cardColors.length];
          const hasDiscount = item.discount && item.discount > 0;
          const discountedPrice = hasDiscount
            ? (item.price - (item.price * item.discount) / 100).toFixed(2)
            : null;

          return (
            <Col md={4} lg={3} key={item._id}>
              <Card
                className="h-100 shadow-sm"
                style={{ backgroundColor: bg, cursor: "pointer" }}
                onClick={() => goToProductDetail(item._id)}
              >
                <Card.Img
                  variant="top"
                  src={item.images?.[0] || "https://via.placeholder.com/150"}
                  style={{ height: "180px", objectFit: "cover" }}
                  alt={item.name || "Product image"}
                />
                <Card.Body>
                  <Card.Title>
                    {item.Tamil ? `${item.Tamil} - ${item.name}` : item.name}
                  </Card.Title>
                  <Card.Subtitle className="text-muted mb-2">
                    Sold by: {item.vendorName || "Unknown Vendor"}
                  </Card.Subtitle>

                  {hasDiscount ? (
                    <div>
                      <span className="text-danger fw-bold me-2">
                        ₹{discountedPrice}
                      </span>
                      <span className="text-decoration-line-through text-muted">
                        ₹{item.price.toFixed(2)}
                      </span>{" "}
                      <span className="text-success">-{item.discount}%</span>
                    </div>
                  ) : (
                    <div className="fw-bold">₹{item.price.toFixed(2)}</div>
                  )}

                  <Card.Text className="mt-2 text-secondary">
                    {item.description?.slice(0, 60)}...
                  </Card.Text>

                  {/* 🔹 GST Info */}
                  <Card.Text className="mt-1 text-secondary small">
                    <strong>HSN:</strong> {item.hsn} |{" "}
                    <strong>GST Rate:</strong> {item.gstRate}% |{" "}
                    <strong>CGST:</strong> {item.cgst}% |{" "}
                    <strong>SGST:</strong> {item.sgst}% |{" "}
                    <strong>IGST:</strong> {item.igst}%
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>

      <div className="text-center mt-4">
        {isFetching && hasMore && <Spinner animation="border" />}
        {!isFetching && hasMore && (
          <Button variant="primary" onClick={loadMore}>
            Load More
          </Button>
        )}
        {!hasMore && filteredProducts.length > 0 && (
          <p className="text-muted mt-3">No more products to load.</p>
        )}
      </div>
    </Container>
  );
};

export default ProductListScreen;
